/** Every robot on the organisation's relay that is online right now. */
import { json, useRobots, useSubscriptionMap, useTracks } from "adamo-react";
import { Link } from "react-router-dom";

import { useConnection } from "../adamo/connection";
import { STEREO_TRACK } from "../adamo/topics";
import type { Driver, Status } from "../adamo/types";

function RobotCard({ id, driver, status }: { id: string; driver: Driver; status: Status | undefined }) {
  const tracks = useTracks(id);
  const active = tracks.filter((t) => t.isActive).map((t) => t.name);
  const held = driver && driver.lease_ok ? (driver.display_name || "someone") : null;
  return (
    <Link className="card" to={`/robots/${encodeURIComponent(id)}`}>
      <div className="name">
        <span className={`dot ${held ? "busy" : "live"}`} />
        {id}
      </div>
      <div className="line">
        {status ? `${status.state}${status.paused ? ", paused" : ""} · ${status.cycle} pick${status.cycle === 1 ? "" : "s"} this session`
                : "no status yet"}
      </div>
      <div className="line">{held ? `driven by ${held}` : "free to drive"}</div>
      <div className="tags">
        {active.length === 0 && <span className="tag">no video</span>}
        {active.map((name) => (
          <span key={name} className={`tag ${name === STEREO_TRACK ? "on" : ""}`}>{name}</span>
        ))}
      </div>
    </Link>
  );
}

export function RobotsPage() {
  const { org } = useConnection();
  const robots = useRobots();
  // One subscription for the whole list rather than one per row: the wildcard is what the
  // relay is good at, and a fleet page should not open a socket's worth of work per robot.
  // Keyed off `org` and disabled until it arrives: the relay refuses any key outside
  // `adamo/{org}/`, and it refuses it silently.
  const statuses = useSubscriptionMap<Status>(org ? `adamo/${org}/*/status` : "",
                                              (bytes) => json.decode<Status>(bytes),
                                              { enabled: Boolean(org) });
  const drivers = useSubscriptionMap<Driver>(org ? `adamo/${org}/*/state/driver` : "",
                                             (bytes) => json.decode<Driver>(bytes),
                                             { enabled: Boolean(org) });

  const pick = <T,>(map: Map<string, T>, id: string): T | undefined => {
    for (const [key, value] of map) if (key.split("/")[2] === id) return value;
    return undefined;
  };

  return (
    <>
      <h1>Live sessions</h1>
      {robots.length === 0 ? (
        <p className="empty">
          {org ? `Nothing is online in ${org} right now.` : "Waiting for the relay…"}
        </p>
      ) : (
        <div className="cards">
          {robots.map((robot) => (
            <RobotCard key={robot.id} id={robot.id}
                       driver={pick(drivers, robot.id) ?? null}
                       status={pick(statuses, robot.id)} />
          ))}
        </div>
      )}
    </>
  );
}
